import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { messageAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatDateTime, getInitials } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { MessageSquare, Send, ArrowLeft, Loader2, Plus, Users, Paperclip, X, Image, FileText, File, Download, FolderOpen, Megaphone } from 'lucide-react';

export default function MessagesPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(null);
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [announceSubject, setAnnounceSubject] = useState('');
  const [announceContent, setAnnounceContent] = useState('');
  const [sendingAnnounce, setSendingAnnounce] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const canAnnounce = user?.role === 'landlord' || user?.role === 'property_manager';

  const handleSendAnnouncement = async () => {
    if (!announceContent.trim()) { toast.error('Enter an announcement message'); return; }
    setSendingAnnounce(true);
    try {
      const { data } = await messageAPI.announce({
        subject: announceSubject.trim() || 'Announcement',
        content: announceContent.trim(),
      });
      if (data.sent_count > 0) {
        toast.success(`Announcement sent to ${data.sent_count} tenant${data.sent_count > 1 ? 's' : ''}`);
      } else {
        toast.info(data.message || 'No active-lease tenants to announce to');
      }
      setShowAnnounce(false);
      setAnnounceSubject('');
      setAnnounceContent('');
      loadConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setSendingAnnounce(false);
    }
  };

  useEffect(() => {
    loadConversations();
    loadContacts();
  }, []);

  useEffect(() => {
    if (selectedPartner) {
      loadMessages(selectedPartner.user_id);
    }
  }, [selectedPartner]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    try {
      const response = await messageAPI.getConversations();
      setConversations(response.data.filter(conv => conv.partner !== null));
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await messageAPI.getContacts();
      setContacts(response.data);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const loadMessages = async (partnerId) => {
    try {
      const response = await messageAPI.getConversation(partnerId);
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - now supporting images, PDFs, and documents
    const allowedTypes = {
      'image/jpeg': { category: 'image', maxSize: 5 },
      'image/png': { category: 'image', maxSize: 5 },
      'image/gif': { category: 'image', maxSize: 5 },
      'image/webp': { category: 'image', maxSize: 5 },
      'application/pdf': { category: 'document', maxSize: 10 },
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { category: 'document', maxSize: 10 },
      'application/msword': { category: 'document', maxSize: 10 },
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { category: 'spreadsheet', maxSize: 10 },
      'application/vnd.ms-excel': { category: 'spreadsheet', maxSize: 10 },
    };
    
    if (!allowedTypes[file.type]) {
      toast.error('Supported: Images (JPG, PNG, GIF), Documents (PDF, DOC, DOCX), Spreadsheets (XLS, XLSX)');
      return;
    }

    const fileInfo = allowedTypes[file.type];
    const maxSizeMB = fileInfo.maxSize;
    
    // Validate file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File size must be less than ${maxSizeMB}MB`);
      return;
    }

    setAttachment({ file, category: fileInfo.category, name: file.name, size: file.size });
    
    // Create preview for images only
    if (fileInfo.category === 'image') {
      const reader = new FileReader();
      reader.onload = (e) => setAttachmentPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !attachment) || !selectedPartner) return;

    setSending(true);
    try {
      let attachmentData = null;

      // Upload attachment if present
      if (attachment) {
        setUploadingAttachment(true);
        const uploadResponse = await messageAPI.uploadAttachment(attachment.file);
        attachmentData = {
          url: uploadResponse.data.url,
          type: uploadResponse.data.content_type,
          name: uploadResponse.data.original_name || attachment.name,
          category: uploadResponse.data.category || attachment.category,
          size: uploadResponse.data.size || attachment.size
        };
        setUploadingAttachment(false);
      }

      const contentPrefix = attachment?.category === 'image' ? '📷 Image' 
        : attachment?.category === 'document' ? '📄 Document' 
        : attachment?.category === 'spreadsheet' ? '📊 Spreadsheet' 
        : '';

      await messageAPI.sendMessage({
        receiver_id: selectedPartner.user_id,
        subject: 'Message',
        content: newMessage.trim() || contentPrefix,
        attachment_url: attachmentData?.url,
        attachment_type: attachmentData?.type,
        attachment_name: attachmentData?.name,
        attachment_category: attachmentData?.category,
        attachment_size: attachmentData?.size,
      });
      
      setNewMessage('');
      removeAttachment();
      loadMessages(selectedPartner.user_id);
      loadConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
      setUploadingAttachment(false);
    }
  };

  const handleStartConversation = (contact) => {
    setSelectedPartner(contact);
    setShowNewConversation(false);
    const existingConv = conversations.find(c => c.partner?.user_id === contact.user_id);
    if (existingConv) {
      loadMessages(contact.user_id);
    } else {
      setMessages([]);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">{t('messages.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('messages.communicateWith')} {user?.role === 'tenant' ? t('tenants.landlord') : t('nav.tenants').toLowerCase()}
          </p>
        </div>
        {contacts.length > 0 && (
          <Button 
            onClick={() => setShowNewConversation(true)}
            className="bg-[#C85A42] hover:bg-[#B04A35]"
            data-testid="new-conversation-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('messages.newMessage')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-250px)] min-h-[500px]">
        {/* Conversations List */}
        <Card className={`lg:col-span-1 ${selectedPartner ? 'hidden lg:block' : ''}`}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              {t('messages.conversations')}
              {conversations.length > 0 && (
                <Badge variant="secondary">{conversations.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {conversations.length === 0 && contacts.length === 0 ? (
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                  <MessageSquare className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground text-sm">
                  {user?.role === 'tenant' 
                    ? t('messages.noLeaseForMessaging')
                    : t('messages.noTenantsYet')}
                </p>
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                  <MessageSquare className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground text-sm mb-3">{t('messages.noMessages')}</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowNewConversation(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('messages.startConversation')}
                </Button>
              </div>
            ) : (
              <div className="divide-y max-h-[400px] overflow-y-auto">
                {conversations.map((conv) => (
                  <div
                    key={conv.partner?.user_id}
                    className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${
                      selectedPartner?.user_id === conv.partner?.user_id ? 'bg-muted' : ''
                    }`}
                    onClick={() => setSelectedPartner(conv.partner)}
                    data-testid={`conversation-${conv.partner?.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={conv.partner?.picture} />
                        <AvatarFallback className="bg-[#6B8E6B] text-white">
                          {getInitials(conv.partner?.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium truncate">{conv.partner?.name}</p>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-[#C85A42] text-white text-xs">
                              {conv.unread_count}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                          {conv.last_message?.attachment_url && <Image className="w-3 h-3" />}
                          {conv.last_message?.content}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Messages */}
        <Card className={`lg:col-span-2 flex flex-col ${!selectedPartner ? 'hidden lg:flex' : ''}`}>
          {selectedPartner ? (
            <>
              {/* Header */}
              <div className="p-4 border-b flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  onClick={() => setSelectedPartner(null)}
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <Avatar className="w-10 h-10">
                  <AvatarImage src={selectedPartner.picture} />
                  <AvatarFallback className="bg-[#6B8E6B] text-white">
                    {getInitials(selectedPartner.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{selectedPartner.name}</p>
                  <p className="text-sm text-muted-foreground capitalize">{selectedPartner.role}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>{t('messages.noMessagesYet')}</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                          msg.sender_id === user?.user_id 
                            ? 'bg-[#C85A42] text-white rounded-br-md' 
                            : 'bg-muted rounded-bl-md'
                        }`}
                      >
                        {/* Attachment rendering */}
                        {(msg.attachment_url || msg.attachment?.url || msg.attachment?.doc_id) && (
                          <div className="mb-2 space-y-2">
                            {/* Image attachment */}
                            {(msg.attachment?.category === 'image' || (msg.attachment_type || '').startsWith('image/')) ? (
                              <div 
                                className="cursor-pointer"
                                onClick={() => setShowImagePreview(getFullImageUrl(msg.attachment?.url || msg.attachment_url))}
                              >
                                <img 
                                  src={getFullImageUrl(msg.attachment?.url || msg.attachment_url)} 
                                  alt="Attachment" 
                                  className="max-w-full rounded-lg max-h-48 object-cover"
                                />
                              </div>
                            ) : (msg.attachment?.url || msg.attachment_url) ? (
                              /* Document/Spreadsheet attachment with download URL */
                              <a
                                href={getFullImageUrl(msg.attachment?.url || msg.attachment_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-3 p-3 rounded-lg ${
                                  msg.sender_id === user?.user_id 
                                    ? 'bg-white/10 hover:bg-white/20' 
                                    : 'bg-background hover:bg-muted'
                                }`}
                              >
                                <div className={`p-2 rounded-lg ${
                                  msg.sender_id === user?.user_id ? 'bg-white/20' : 'bg-[#C85A42]/10'
                                }`}>
                                  {(msg.attachment?.category === 'spreadsheet' || (msg.attachment_type || '').includes('sheet')) ? (
                                    <File className={`w-5 h-5 ${msg.sender_id === user?.user_id ? 'text-white' : 'text-green-600'}`} />
                                  ) : (
                                    <FileText className={`w-5 h-5 ${msg.sender_id === user?.user_id ? 'text-white' : 'text-[#C85A42]'}`} />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium truncate ${
                                    msg.sender_id === user?.user_id ? 'text-white' : ''
                                  }`}>
                                    {msg.attachment?.name || 'Document'}
                                  </p>
                                  {msg.attachment?.size && (
                                    <p className={`text-xs ${
                                      msg.sender_id === user?.user_id ? 'text-white/70' : 'text-muted-foreground'
                                    }`}>
                                      {(msg.attachment.size / 1024).toFixed(1)} KB
                                    </p>
                                  )}
                                </div>
                                <Download className={`w-4 h-4 ${
                                  msg.sender_id === user?.user_id ? 'text-white' : 'text-muted-foreground'
                                }`} />
                              </a>
                            ) : msg.attachment?.doc_id ? (
                              /* Note attachment (no URL, content lives in Documents) */
                              <div className={`flex items-center gap-3 p-3 rounded-lg ${
                                msg.sender_id === user?.user_id ? 'bg-white/10' : 'bg-background'
                              }`}>
                                <div className={`p-2 rounded-lg ${
                                  msg.sender_id === user?.user_id ? 'bg-white/20' : 'bg-violet-100 dark:bg-violet-900/40'
                                }`}>
                                  <FileText className={`w-5 h-5 ${msg.sender_id === user?.user_id ? 'text-white' : 'text-violet-600'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium truncate ${msg.sender_id === user?.user_id ? 'text-white' : ''}`}>
                                    {msg.attachment?.name || 'Shared note'}
                                  </p>
                                  <p className={`text-xs ${msg.sender_id === user?.user_id ? 'text-white/70' : 'text-muted-foreground'}`}>
                                    Shared note
                                  </p>
                                </div>
                              </div>
                            ) : null}

                            {/* Deep link to Documents page when this message references a shared doc */}
                            {msg.attachment?.doc_id && user?.role === 'tenant' && (
                              <Button
                                size="sm"
                                variant={msg.sender_id === user?.user_id ? 'secondary' : 'outline'}
                                className="w-full"
                                onClick={() => navigate('/tenant/documents')}
                                data-testid={`open-in-documents-${msg.message_id}`}
                              >
                                <FolderOpen className="w-3.5 h-3.5 mr-1.5" />
                                Open in Documents
                              </Button>
                            )}
                          </div>
                        )}
                        {msg.content && !['📷 Image', '📄 Document', '📊 Spreadsheet'].includes(msg.content) && (
                          <p className="break-words">{msg.content}</p>
                        )}
                        <p className={`text-xs mt-1 ${
                          msg.sender_id === user?.user_id ? 'text-white/70' : 'text-muted-foreground'
                        }`}>
                          {formatDateTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Attachment Preview */}
              {attachment && (
                <div className="px-4 py-2 border-t bg-muted/30">
                  <div className="relative inline-block">
                    {attachment.category === 'image' && attachmentPreview ? (
                      <img 
                        src={attachmentPreview} 
                        alt="Attachment preview" 
                        className="h-20 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-background rounded-lg border">
                        <div className="p-2 rounded-lg bg-[#C85A42]/10">
                          {attachment.category === 'spreadsheet' ? (
                            <File className="w-5 h-5 text-green-600" />
                          ) : (
                            <FileText className="w-5 h-5 text-[#C85A42]" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate max-w-[150px]">{attachment.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(attachment.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                    )}
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                      onClick={removeAttachment}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  data-testid="attach-image-btn"
                >
                  <Paperclip className="w-5 h-5" />
                </Button>
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t('messages.typeMessage')}
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button
                  type="submit"
                  disabled={sending || (!newMessage.trim() && !attachment)}
                  className="bg-[#C85A42] hover:bg-[#B04A35]"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-6">
              <div>
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{t('messages.selectConversation')}</h3>
                <p className="text-muted-foreground mb-4">
                  {t('messages.chooseConversation')}
                </p>
                {contacts.length > 0 && (
                  <Button 
                    variant="outline"
                    onClick={() => setShowNewConversation(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('messages.startConversation')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={showNewConversation} onOpenChange={setShowNewConversation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {t('messages.startConversation')}
            </DialogTitle>
            <DialogDescription>
              {t('messages.selectToMessage')} {user?.role === 'tenant' ? t('tenants.landlord').toLowerCase() : t('nav.tenants').toLowerCase()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {canAnnounce && (
              <button
                type="button"
                onClick={() => { setShowNewConversation(false); setShowAnnounce(true); }}
                className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-[#C85A42]/40 hover:bg-[#C85A42]/5 transition-colors text-left"
                data-testid="announce-option-btn"
              >
                <div className="w-10 h-10 rounded-lg bg-[#C85A42] flex items-center justify-center shrink-0">
                  <Megaphone className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-[#C85A42]">Announce to all tenants</p>
                  <p className="text-sm text-muted-foreground">Send one message to every active-lease tenant</p>
                </div>
              </button>
            )}
            {contacts.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <p>
                  {user?.role === 'tenant' 
                    ? t('messages.noLeaseForMessaging')
                    : t('messages.noTenantsYet')}
                </p>
              </div>
            ) : (
              contacts.map((contact) => (
                <div
                  key={contact.user_id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                  onClick={() => handleStartConversation(contact)}
                  data-testid={`contact-${contact.user_id}`}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={contact.picture} />
                    <AvatarFallback className="bg-[#6B8E6B] text-white">
                      {getInitials(contact.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium">{contact.name}</p>
                    <p className="text-sm text-muted-foreground">{contact.email}</p>
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {contact.role}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Announcement Composer Dialog */}
      <Dialog open={showAnnounce} onOpenChange={setShowAnnounce}>
        <DialogContent data-testid="announce-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#C85A42]" />
              Announce to All Tenants
            </DialogTitle>
            <DialogDescription>
              This message is sent to every tenant with an active lease. It appears in their Messages inbox and as a notification. Tenants can reply directly to you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Subject (optional)</label>
              <Input
                value={announceSubject}
                onChange={(e) => setAnnounceSubject(e.target.value)}
                placeholder="e.g., Water interruption notice"
                data-testid="announce-subject-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Message</label>
              <textarea
                value={announceContent}
                onChange={(e) => setAnnounceContent(e.target.value)}
                placeholder="Type your announcement to all tenants..."
                rows={5}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="announce-content-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowAnnounce(false)}>Cancel</Button>
            <Button
              onClick={handleSendAnnouncement}
              disabled={sendingAnnounce || !announceContent.trim()}
              className="bg-[#C85A42] hover:bg-[#B04A35]"
              data-testid="send-announce-btn"
            >
              {sendingAnnounce ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Megaphone className="w-4 h-4 mr-2" />}
              Send to All
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!showImagePreview} onOpenChange={() => setShowImagePreview(null)}>
        <DialogContent className="max-w-3xl p-2">
          {showImagePreview && (
            <img 
              src={showImagePreview} 
              alt="Full size preview" 
              className="w-full h-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
